import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on app start and set up periodic session checks
  useEffect(() => {
    checkAuthStatus();
    
    // Function to check session expiration
    const checkSessionExpiration = () => {
      const loginTime = sessionStorage.getItem('loginTime');
      
      if (loginTime && isAuthenticated) {
        const currentTime = new Date().getTime();
        const timeDiff = currentTime - parseInt(loginTime);
        const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
        
        if (timeDiff > thirtyMinutes) {
          // Session expired, logout user
          logout();
          alert('Your session has expired. Please login again.');
        }
      }
    };
    
    // Set up periodic session expiration check every minute
    const sessionCheckInterval = setInterval(() => {
      if (isAuthenticated) {
        checkSessionExpiration();
      }
    }, 60000); // Check every minute

    // Cleanup interval on unmount
    return () => clearInterval(sessionCheckInterval);
  }, [isAuthenticated]);

  const checkAuthStatus = async () => {
    const token = sessionStorage.getItem('authToken');
    const loginTime = sessionStorage.getItem('loginTime');
    
    if (!token) {
      setLoading(false);
      return;
    }

    // Check if 30 minutes have passed since login
    if (loginTime) {
      const currentTime = new Date().getTime();
      const timeDiff = currentTime - parseInt(loginTime);
      const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
      
      if (timeDiff > thirtyMinutes) {
        // Session expired, logout user
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('loginTime');
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
    }

    try {
      const response = await fetch('/api/auth/check', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setUser(result.user);
        setIsAuthenticated(true);
      } else {
        // Token is invalid, remove it
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('loginTime');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('loginTime');
      setUser(null);
      setIsAuthenticated(false);
    }

    setLoading(false);
  };

  const login = (userData) => {
    // Store login time for session expiration tracking
    const currentTime = new Date().getTime();
    sessionStorage.setItem('loginTime', currentTime.toString());
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      if (token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('loginTime');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Function to make authenticated API requests
  const authenticatedFetch = async (url, options = {}) => {
    const token = sessionStorage.getItem('authToken');
    const loginTime = sessionStorage.getItem('loginTime');
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Check if session has expired (30 minutes)
    if (loginTime) {
      const currentTime = new Date().getTime();
      const timeDiff = currentTime - parseInt(loginTime);
      const thirtyMinutes = 30 * 60 * 1000;
      
      if (timeDiff > thirtyMinutes) {
        logout();
        throw new Error('Session expired. Please login again.');
      }
    }

    const config = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };

    const response = await fetch(url, config);

    // If token is expired or invalid, logout user
    if (response.status === 401 || response.status === 403) {
      logout();
      throw new Error('Session expired. Please login again.');
    }

    return response;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    authenticatedFetch,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
